import type React from 'react'
import {
  dashboardTypeLabels,
  dashboardTypeLabelSingular,
  type DashboardTypes,
} from '@github-ui/shared-dashboard-sidebar/types/dashboard-types'
import {
  AlertIcon,
  GitPullRequestIcon,
  InboxIcon,
  IssueOpenedIcon,
  RepoIcon,
  SearchIcon,
  type Icon,
} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {Link} from '@primer/react'
import {getEmptyListMessages} from './helpers/blankslate-helpers'
import type {EmptyListState} from './types/blankslate-types'
import {clsx} from 'clsx'

type SharedDashboardBlankslateVariant = 'emptyInbox' | 'error' | 'empty' | 'emptySearch'

function getSearchVariantIcon(dashboardType: DashboardTypes): Icon {
  switch (dashboardType) {
    case 'pull-requests':
      return GitPullRequestIcon
    case 'repositories':
      return RepoIcon
    case 'issues':
      return IssueOpenedIcon
    default:
      return SearchIcon
  }
}

function getInboxEmptyDescription(dashboardType: DashboardTypes): string | undefined {
  const dashboardLabel = dashboardTypeLabelSingular[dashboardType]
  return `When a ${dashboardLabel} needs your review or attention, it'll appear here.`
}

function getErrorDescription(dashboardType: DashboardTypes): string {
  const dashboardLabel = dashboardTypeLabels[dashboardType]
  return `Failed to load ${dashboardLabel}. Try refreshing the page.`
}

function getSearchHelpLink(dashboardType: DashboardTypes): React.ReactNode | undefined {
  const dashboardLabel = dashboardTypeLabels[dashboardType]

  if (dashboardType === 'pull-requests' || dashboardType === 'issues') {
    return (
      <Link
        inline
        href="https://docs.github.com/en/issues/tracking-your-work-with-issues/using-issues/filtering-and-searching-issues-and-pull-requests"
      >
        Learn more about searching and filtering {dashboardLabel}.
      </Link>
    )
  }
}

function getEmptySearchDescription(dashboardType: DashboardTypes): React.ReactNode {
  const dashboardLabel = dashboardTypeLabels[dashboardType]
  const link = getSearchHelpLink(dashboardType)

  return (
    <>
      <span data-testid="empty-search-description">Search for {dashboardLabel}.</span>
      {link ? <> {link}</> : null}
    </>
  )
}

function getEmptyDescription(dashboardType: DashboardTypes, emptyListState?: EmptyListState): React.ReactNode {
  const messages = emptyListState ? getEmptyListMessages(emptyListState) : undefined
  const description = emptyListState ? messages?.description : 'Try a different search query.'
  const link = getSearchHelpLink(dashboardType)

  if (!description && !link) return undefined

  return (
    <>
      {description}
      {link ? <> {link}</> : null}
    </>
  )
}

function getVariants(dashboardType: DashboardTypes, emptyListState?: EmptyListState) {
  const emptyMessages = emptyListState ? getEmptyListMessages(emptyListState) : undefined

  return {
    emptyInbox: {
      icon: InboxIcon,
      heading: 'All caught up',
      description: getInboxEmptyDescription(dashboardType),
    },
    error: {
      icon: AlertIcon,
      heading: 'Something went wrong',
      description: getErrorDescription(dashboardType),
    },
    empty: {
      icon: getSearchVariantIcon(dashboardType),
      heading: emptyMessages?.heading ?? `No ${dashboardTypeLabels[dashboardType]} matched your search`,
      description: getEmptyDescription(dashboardType, emptyListState),
    },
    emptySearch: {
      icon: SearchIcon,
      heading: 'Try a new search',
      description: getEmptySearchDescription(dashboardType),
    },
  } as const
}

interface SharedDashboardBlankslateProps {
  dashboardType: DashboardTypes
  variant: SharedDashboardBlankslateVariant
  emptyListState?: EmptyListState
  descriptionOverride?: React.ReactNode
}

export function SharedDashboardBlankslate({
  dashboardType,
  variant,
  emptyListState,
  descriptionOverride,
}: SharedDashboardBlankslateProps) {
  const variants = getVariants(dashboardType, emptyListState)
  const {icon: Icon, heading, description: defaultDescription} = variants[variant]
  const description = descriptionOverride ?? defaultDescription
  const role = variant === 'error' ? 'alert' : 'status'

  return (
    <div
      className={clsx(
        'border',
        'borderColor-muted',
        'rounded-2',
        variant === 'emptySearch' && 'tmp-mt-3',
        variant === 'error' && 'tmp-pt-3',
      )}
      role={role}
    >
      <Blankslate border={false} spacious={false}>
        <Blankslate.Visual>
          <Icon size={24} />
        </Blankslate.Visual>
        <Blankslate.Heading>{heading}</Blankslate.Heading>
        {description ? <Blankslate.Description>{description}</Blankslate.Description> : null}
        {variant === 'error' && (
          <Blankslate.SecondaryAction href="https://www.githubstatus.com/">GitHub status</Blankslate.SecondaryAction>
        )}
      </Blankslate>
    </div>
  )
}
